import React, { useEffect, useMemo, useRef, useState } from 'react';
import moment from 'moment';
import { Link, useSearchParams } from 'react-router-dom';
import cx from 'classnames';
import { MessageSquare, Plus, Search } from 'lucide-react';
import { useFetchMessagesQuery } from '@@/services/message';
import { normalizeText } from '@@/utils/string';

import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';

const MESSAGES_LIMIT = 500;
const SEARCH_DEBOUNCE_MS = 300;
const stateCardClassName =
  'bg-white rounded-card shadow-card border border-neutral-200 p-8 text-center';

// Pick avoids deep type instantiation from DiscussionDocument (extends File)
type MessageRow = Pick<
  Alacaza.ScheduledMessage,
  'id' | 'scheduledAt' | 'message' | 'postId' | 'sentAt' | 'propertyName'
>;

const columnHelper = createColumnHelper<MessageRow>();
const columns = [
  columnHelper.accessor('propertyName', {
    header: 'Résidence',
    cell: (row) => {
      const propertyName = row.getValue();

      return (
        <p className="font-semibold text-neutral-900 text-body">
          {propertyName}
        </p>
      );
    },
  }),
  columnHelper.accessor((row) => row, {
    header: 'Message',
    cell: (row) => {
      const { message, postId } = row.getValue();

      return (
        <p className="text-neutral-900 text-body">
          {postId ? 'Sondage' : message}
        </p>
      );
    },
  }),
  columnHelper.accessor((row) => row, {
    header: "Date d'envoi",
    cell: (row) => {
      const { scheduledAt, sentAt } = row.getValue();

      return (
        <div className="flex flex-row items-center justify-center gap-2">
          <p className="font-semibold text-neutral-900 text-body-sm">
            {moment(sentAt || scheduledAt).format('lll')}
          </p>
          <div
            className={cx(
              'h-2.5 w-2.5 rounded-full flex-shrink-0',
              !!sentAt ? 'bg-success-500' : 'bg-warning-500',
            )}
          />
        </div>
      );
    },
  }),
  columnHelper.accessor((row) => row, {
    header: 'Actions',
    cell: (row) => {
      const { id } = row.getValue();

      return (
        <div className="flex justify-center">
          <Link
            to={`/messages/${id}`}
            className="bg-neutral-500 hover:bg-neutral-600 px-4 py-2 rounded-button shadow-button transition-colors inline-flex items-center"
          >
            <span className="text-white text-body-sm font-medium">Détails</span>
          </Link>
        </div>
      );
    },
  }),
];

const buildSearchText = (message: Alacaza.ScheduledMessage) =>
  normalizeText(
    [
      message.postId && 'Sondage',
      message.message,
      message.propertyName,
      message.createdByUserName,
      message.discussionDocument?.originalFilename ||
        message.discussionDocument?.filename,
    ]
      .filter(Boolean)
      .join('\n'),
  );

const MessagesPage: React.FC = () => {
  const [query, setQuery] = useSearchParams();
  const propertyId = query.get('propertyId') || undefined;
  const status = query.get('status') || undefined;
  const search = query.get('search') || '';
  const {
    currentData: messages,
    isFetching,
    error,
  } = useFetchMessagesQuery({
    propertyId,
    status,
    page: 1,
    limit: MESSAGES_LIMIT,
  });
  const isLoadingMessages = isFetching && !messages;

  // Input state is owned locally so URL updates never overwrite in-progress typing
  const [searchInput, setSearchInput] = useState(search);
  const lastPushedSearch = useRef(search);
  const latestQuery = useRef({ query, setQuery });
  latestQuery.current = { query, setQuery };

  // Sync input when the URL search changes from outside (e.g. history navigation)
  useEffect(() => {
    if (search !== lastPushedSearch.current) {
      lastPushedSearch.current = search;
      setSearchInput(search);
    }
  }, [search]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      const { query: currentQuery, setQuery: updateQuery } =
        latestQuery.current;
      const nextSearch = searchInput.trim() ? searchInput : '';

      if (nextSearch === (currentQuery.get('search') || '')) return;

      const next = new URLSearchParams(currentQuery);
      if (nextSearch) {
        next.set('search', nextSearch);
      } else {
        next.delete('search');
      }
      lastPushedSearch.current = nextSearch;
      updateQuery(next, { replace: true });
    }, SEARCH_DEBOUNCE_MS);

    return () => clearTimeout(timeout);
  }, [searchInput]);

  const searchIndex = useMemo(
    () =>
      (messages?.docs || []).map((message) => ({
        message,
        text: buildSearchText(message),
      })),
    [messages],
  );

  const normalizedSearch = normalizeText(search.trim());
  const filteredMessages = useMemo(
    () =>
      searchIndex
        .filter(
          ({ text }) => !normalizedSearch || text.includes(normalizedSearch),
        )
        .map(({ message }) => message),
    [searchIndex, normalizedSearch],
  );

  const table = useReactTable({
    data: filteredMessages,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  const totalCount = messages?.docs?.length || 0;
  const hasMessages = totalCount > 0;
  const isTruncated = totalCount >= MESSAGES_LIMIT;
  const emptyLabel = hasMessages
    ? `Aucun message ne correspond à « ${search.trim()} »`
    : 'Aucun message trouvé';

  return (
    <div className="px-page-sm py-6 lg:px-page lg:py-8 w-full">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-6 gap-4">
          <div className="flex flex-row items-center">
            <MessageSquare size={28} className="text-neutral-900" />
            <h1 className="text-heading-lg text-neutral-900 ml-3">
              Communication
            </h1>
          </div>
          <Link
            to="/messages/new"
            className="bg-primary-600 hover:bg-primary-700 px-4 py-2.5 rounded-button shadow-button transition-colors flex items-center space-x-2"
          >
            <Plus size={20} className="text-white" />
            <span className="text-white text-body-sm font-medium">
              Nouveau message
            </span>
          </Link>
        </div>

        {/* Search */}
        <div className="relative mb-6">
          <Search
            size={18}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400 pointer-events-none"
          />
          <input
            type="search"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Rechercher un message"
            aria-label="Rechercher un message"
            className="w-full pl-10 pr-4 py-2.5 bg-white border border-neutral-200 rounded-button text-body-sm text-neutral-900 placeholder:text-neutral-400 focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
        </div>

        {isTruncated && !!normalizedSearch && (
          <p className="-mt-4 mb-6 text-body-sm text-neutral-500">
            Recherche limitée aux {MESSAGES_LIMIT} messages les plus récents.
          </p>
        )}

        {isLoadingMessages ? (
          <div className={stateCardClassName}>
            <p className="text-body text-neutral-500">Chargement...</p>
          </div>
        ) : error ? (
          <div className={stateCardClassName}>
            <p className="text-body text-danger-600">
              Erreur lors du chargement des messages
            </p>
          </div>
        ) : (
          <>
            {/* Mobile Cards - visible < lg */}
            <div className="lg:hidden space-y-4">
              {filteredMessages.map((message) => (
                <div
                  key={message.id}
                  className="bg-white rounded-card shadow-card border border-neutral-200 p-4 hover:shadow-card-hover transition-shadow"
                >
                  <div className="mb-3">
                    <p className="font-semibold text-neutral-900 text-body-sm mb-1">
                      {message.propertyName}
                    </p>
                    <p className="text-neutral-900 text-body mb-2">
                      {message.postId ? 'Sondage' : message.message}
                    </p>
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <p className="text-neutral-600 text-body-sm">
                        {moment(message.sentAt || message.scheduledAt).format(
                          'lll',
                        )}
                      </p>
                      <div
                        className={cx(
                          'h-2.5 w-2.5 rounded-full flex-shrink-0',
                          !!message.sentAt
                            ? 'bg-success-500'
                            : 'bg-warning-500',
                        )}
                      />
                    </div>

                    <Link
                      to={`/messages/${message.id}`}
                      className="bg-neutral-500 hover:bg-neutral-600 px-4 py-2 rounded-button shadow-button transition-colors inline-flex items-center"
                    >
                      <span className="text-white text-body-sm font-medium">
                        Détails
                      </span>
                    </Link>
                  </div>
                </div>
              ))}

              {filteredMessages.length === 0 && (
                <div className={stateCardClassName}>
                  <p className="text-body text-neutral-500">{emptyLabel}</p>
                </div>
              )}
            </div>

            {/* Desktop Table - visible >= lg */}
            <div className="hidden lg:block bg-white rounded-card shadow-card border border-neutral-200 overflow-hidden">
              <div className="px-6 py-4 border-b border-neutral-200">
                <p className="text-body-sm font-semibold text-neutral-700">
                  {normalizedSearch
                    ? `${filteredMessages.length} / ${totalCount} message${totalCount > 1 ? 's' : ''}`
                    : `${totalCount} message${totalCount > 1 ? 's' : ''}`}
                </p>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    {table.getHeaderGroups().map((headerGroup) => (
                      <tr key={headerGroup.id}>
                        {headerGroup.headers.map((header) => (
                          <th
                            key={header.id}
                            className="px-4 py-3 text-left text-body-sm font-semibold text-white bg-neutral-700 border border-neutral-200"
                          >
                            {header.isPlaceholder
                              ? null
                              : flexRender(
                                  header.column.columnDef.header,
                                  header.getContext(),
                                )}
                          </th>
                        ))}
                      </tr>
                    ))}
                  </thead>
                  <tbody>
                    {table.getRowModel().rows.map((row) => (
                      <tr
                        key={row.id}
                        className="hover:bg-neutral-50 transition-colors"
                      >
                        {row.getVisibleCells().map((cell) => (
                          <td
                            key={cell.id}
                            className="px-4 py-3 border-b border-neutral-200"
                          >
                            {flexRender(
                              cell.column.columnDef.cell,
                              cell.getContext(),
                            )}
                          </td>
                        ))}
                      </tr>
                    ))}
                    {filteredMessages.length === 0 && (
                      <tr>
                        <td
                          colSpan={columns.length}
                          className="px-4 py-8 text-center"
                        >
                          <p className="text-body text-neutral-500">
                            {emptyLabel}
                          </p>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default MessagesPage;
